/**
 * Scenario content translation.
 *
 * Scenarios are authored in English. A translation is a flat map of JSON paths
 * (`nodes.call-1.transcript.2.text`) to translated strings. `flattenTranslatable()` lists the
 * paths a translator must cover; `localizeScenario()` applies a map and reports coverage.
 */
import type { Scenario } from "./schema";

/** Keys whose string values are human-readable and therefore translatable. */
const TRANSLATABLE_KEYS = new Set([
  "title",
  "tagline",
  "summary",
  "body",
  "text",
  "tell",
  "label",
  "detail",
  "feedback",
  "prompt",
  "context",
  "name",
  "brief",
  "description",
  "objectives",
  "lessons",
  "redFlags",
  "learningObjectives",
  "subject",
  "from",
  "to",
  "facilitatorNotes",
  "roleNotes",
  "voice",
  "audienceNote",
]);

/** Keys that are identifiers or machine values even when they hold strings. */
const NEVER_TRANSLATE = new Set([
  "id",
  "version",
  "type",
  "next",
  "start",
  "node",
  "icon",
  "url",
  "displayNumber",
  "timestamp",
  "tags",
  "roleBonus",
  "availableAfter",
  "voters",
  "var",
  "op",
  "kind",
  "speaker",
  "difficulty",
  "audience",
  "dimension",
  "tag",
  "roleId",
  "else",
  "basedOn",
  "variables",
]);

export type TranslationMap = Record<string, string>;

function walk(value: unknown, path: string[], keyCtx: string | null, out: Map<string, string>): void {
  if (typeof value === "string") {
    if (keyCtx && TRANSLATABLE_KEYS.has(keyCtx)) out.set(path.join("."), value);
    return;
  }
  if (Array.isArray(value)) {
    // Arrays inherit the key of the property that holds them (e.g. `lessons.0`).
    for (let i = 0; i < value.length; i++) walk(value[i], [...path, String(i)], keyCtx, out);
    return;
  }
  if (value && typeof value === "object") {
    for (const [k, v] of Object.entries(value)) {
      if (NEVER_TRANSLATE.has(k)) continue;
      // `roleNotes` is a map roleId → text; its children are translatable regardless of key.
      const childKey = keyCtx === "roleNotes" ? "roleNotes" : k;
      walk(v, [...path, k], childKey, out);
    }
  }
}

/** All translatable (path → English) pairs of a scenario, in stable order. */
export function flattenTranslatable(scenario: Scenario): TranslationMap {
  const out = new Map<string, string>();
  walk(scenario, [], null, out);
  return Object.fromEntries(out);
}

function setPath(obj: unknown, path: string[], value: string): boolean {
  let cur: unknown = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]!;
    if (cur === null || typeof cur !== "object") return false;
    cur = (cur as Record<string, unknown>)[key];
  }
  const last = path[path.length - 1]!;
  if (cur === null || typeof cur !== "object") return false;
  if (!(last in (cur as Record<string, unknown>))) return false;
  (cur as Record<string, unknown>)[last] = value;
  return true;
}

export interface LocalizedScenario {
  scenario: Scenario;
  locale: string;
  /** 0..1 fraction of translatable strings covered. 1 for English. */
  coverage: number;
  missing: string[];
}

export function localizeScenario(
  scenario: Scenario,
  locale: string,
  translation?: TranslationMap,
): LocalizedScenario {
  const paths = flattenTranslatable(scenario);
  const total = Object.keys(paths).length;
  if (!translation || locale === "en") {
    return { scenario, locale: "en", coverage: 1, missing: locale === "en" ? [] : Object.keys(paths) };
  }
  const copy = structuredClone(scenario);
  let covered = 0;
  const missing: string[] = [];
  for (const p of Object.keys(paths)) {
    const t = translation[p];
    if (t?.trim()) {
      if (setPath(copy, p.split("."), t)) covered++;
    } else {
      missing.push(p);
    }
  }
  return { scenario: copy, locale, coverage: total ? covered / total : 1, missing };
}

/** Paths in a translation file that no longer exist in the scenario. */
export function staleTranslationKeys(scenario: Scenario, translation: TranslationMap): string[] {
  const valid = flattenTranslatable(scenario);
  return Object.keys(translation).filter((k) => !(k in valid));
}

export function translationCoverage(scenario: Scenario, translation: TranslationMap): number {
  const paths = Object.keys(flattenTranslatable(scenario));
  if (!paths.length) return 1;
  const covered = paths.filter((p) => translation[p]?.trim()).length;
  return covered / paths.length;
}
